import React from "react";
import { format, isPast } from "date-fns";
import { Button, Table } from "@sscs/ui";
import { CancelIcon, DeleteForeverIcon } from "@sscs/ui/icons";
import { getClassName } from "@sscs/utils";
import { ConfirmButton } from "Components";
import { useDialog } from "Hooks";
import { useGetWhoamiQuery } from "Services";
import { PersonalAccessTokenInfo } from "Types";
import { useRevokeAccessToken } from "../../hooks";
import RevokeAccessToken from "../RevokeAccessToken/RevokeAccessToken";
import styles from "./tokenTableRow.module.scss";

type TokenTableRowProps = {
  token: PersonalAccessTokenInfo;
};

const TokenTableRow = ({ token }: TokenTableRowProps): React.JSX.Element => {
  const revokeTokenDialog = useDialog();

  const { data: whoami } = useGetWhoamiQuery();

  const { handleRevokeAccessToken } = useRevokeAccessToken({
    tokenDigest: token.digest,
    userId: whoami?.user_id,
    errorMessage: "Could not delete personal access token",
    successMessage: "Personal access token deleted",
  });

  const tokenHasExpired = token?.expiry
    ? isPast(new Date(token.expiry))
    : false;

  const rowCls = getClassName(styles["token-table-row"], {
    [styles["token-expired"]]: tokenHasExpired,
  });

  return (
    <>
      <Table.Row className={rowCls}>
        <Table.Cell>{token.name}</Table.Cell>
        <Table.Cell>
          {token?.last_used
            ? format(new Date(token.last_used), "dd/MM/yyyy")
            : "---"}
        </Table.Cell>
        <Table.Cell>
          {token?.expiry
            ? format(new Date(token.expiry), "dd/MM/yyyy")
            : "No expiration date"}
        </Table.Cell>
        <Table.Cell>
          {tokenHasExpired ? (
            <ConfirmButton
              className={styles["delete-button"]}
              color="secondary"
              isFullWidth
              onClick={handleRevokeAccessToken}
              icon={<DeleteForeverIcon />}
            >
              Delete
            </ConfirmButton>
          ) : (
            <Button
              color="secondary"
              isFullWidth
              onClick={revokeTokenDialog.open}
              startIcon={<CancelIcon />}
            >
              Revoke
            </Button>
          )}
        </Table.Cell>
      </Table.Row>
      <RevokeAccessToken
        isOpen={revokeTokenDialog.isOpen}
        onClose={revokeTokenDialog.close}
        token={token}
      />
    </>
  );
};

export default TokenTableRow;
